/**
 * payrollMath — what a subcontractor payslip adds up to, and the payout
 * reference that names it.
 *
 * TRANSCRIBED FROM THE WORKBOOK, NOT REDESIGNED. Every figure below is a cell
 * on "IG OPC - Contractors Payroll - 2025.xlsm":
 *
 *   PAYSLIP (BIR)          NON-BIR PAYSLIP
 *   I68  qty total          I67   qty total
 *   K68  work total         K67   work total
 *   K70  ₱20 × qty          K70   ₱20 × qty      Contractor Additional Charge
 *   K71  12% × work total   —                    Registration & Sales Invoice
 *   K72  ₱200 transpo       K71   ₱200 transpo
 *   K73  free addition      K72   free addition
 *   K74  additions total    —
 *   K81  deductions total   K78   deductions total
 *   K83  NET AMOUNT         —
 *   K84  TOTAL AMOUNT       K80   TOTAL AMOUNT
 *
 * THREE PLACES THIS DELIBERATELY DIVERGES, each because the workbook is
 * demonstrably wrong rather than merely different. All three are surfaced on
 * the payslip screen as notes, so nobody is quietly handed a different number
 * than the paper form would have produced:
 *
 *  1. DEDUCTIONS ARE SUBTRACTED ON BOTH SIDES. BIR's K84 subtracts K81; the
 *     non-BIR sheet computes K78 and then never uses it (K80 = K67+K70+K71+
 *     K72). A recorded cash advance that does not come off the payout is an
 *     overpayment the ledger cannot see, and the BIR sheet already treats it
 *     the other way, so the correct reading is not in doubt.
 *  2. ADMIN TASKS COUNT. Both sheets stop their SUM one row short of the
 *     ADMIN TASKS block (BIR sums K17:K61 with admin on 62-67; non-BIR sums
 *     K16:K60 with admin on 61-66), so a rate typed on an admin row is
 *     displayed and never paid. Here every line counts.
 *  3. EACH LINE IS rate × ITS OWN qty. NON-BIR PAYSLIP!K19 reads `=G19*I20`
 *     — row 19's rate against row 20's quantity, an off-by-one that pays the
 *     ₱80 band for whatever was entered against the ₱85 band.
 *
 * TRANSPO IS OUTSIDE "NET AMOUNT" ON PURPOSE. The workbook's K83 is
 * work + additional charge + 12%, with the ₱200 allowance excluded — an
 * allowance is a reimbursement, not earnings, so the figure that feeds a
 * ₱-per-set reading has to leave it out. `totalAmount` is what gets paid;
 * `netAmount` is what was earned. Both are kept, exactly as the form does.
 *
 * Pure and I/O-free.
 */

/** Whether the subcontractor is BIR-registered — decides the 12% line. */
import { planOfSku } from "../mfg-workcenters/bqc4/grnMix";

export type BirSide = "BIR" | "NON-BIR";

/** One priced row of the payslip's work table. */
/**
 * One QC pass behind a payout line, as it was PAID — the WIP-by-WIP record Mikko asked
 * for on 2026-10-06 ("take note of WIPs that are already paid and its qty paid and qty
 * remaining"). `sets` is what this line paid on the pass; `of` is how many sets of it were
 * still open when the payout was made (QC passed, less what earlier payouts took), so
 * `of - sets` is what was HELD BACK and stays open for the next run (see
 * payoutDraft.collectPaidKeys). `wipQty` is the WIP's quantity in BQC1 at that moment.
 */
export interface IPayoutPass {
  key: string;
  wipId: string;
  sets: number;
  of: number;
  wipQty?: number;
  sku?: string;
  /** The plan the pass was PRICED on, so the payout can say how many pieces it paid. BOTH is written too:
   *  a -TO- SKU a person set to Full set is a set, and without it the SKU's marker would count it a piece
   *  (Mikko, 2026-10-17, on 260909086). Absent only on a payout written before this. */
  plan?: "TOP" | "PANTS" | "BOTH";
}

export interface IPayoutLine {
  /** Rate card key — see rateCard.ts on why this is not the code. */
  rateKey: string;
  /** The workbook's short code, for display. */
  code: string;
  group: string;
  item: string;
  /** Editable per payslip: the card is the default, not a lock. */
  rate: number;
  qty: number;
  /**
   * Where the quantity came from. "bqc5" was derived from QC-passed sets,
   * "manual" was typed, "workbook" came in from the RECORDS OF PAYOUT /
   * NON-BIR RECORDS sheets (payoutLedgerImport.ts). Kept per line so a
   * payslip can always answer "why does it say 42?" — see payoutDraft.ts.
   */
  source: "bqc5" | "manual" | "workbook";
  /** WIP ids the derived quantity came from. Empty for a typed line. */
  wipIds?: readonly string[];
  /**
   * The parent SKUs actually sewn on this line — "028", "031B", "057".
   *
   * A rate band's `item` text lists every design the band COVERS ("SS 028,
   * 030, 031, 031A, 031B, 033, 043, 045, 048, 049, 052"), which is the right
   * thing on a blank rate card and the wrong thing on a payslip: a
   * subcontractor reading their own slip needs to see what they sewed, not
   * the eleven designs they might have. Collected from the WIPs behind the
   * quantity, so it is evidence rather than a label. Absent on a typed line,
   * where the band text is all there is.
   */
  designs?: readonly string[];
  /**
   * The QC pass events this line paid for — "qc:<listId>" for the app's own
   * scans, "wb:<date>:<wip>:<qty>:<serial>" for rows read from the hub's
   * BQC5 sheet (see qcEvents.ts). This is what makes "since the last payout"
   * computable: the next run skips every key already on a saved payslip.
   * Absent on a typed line and on payouts saved before the cursor existed.
   */
  scanKeys?: readonly string[];
  /**
   * The passes behind this line, WIP by WIP, with the sets each one paid and how many were
   * still open (`IPayoutPass`). Absent on a typed line and on every payout written before
   * 2026-10-06 — those spent each key whole, as they always did.
   */
  passes?: readonly IPayoutPass[];
  /**
   * The GARMENTS behind this line's quantity — full SKU CODE and sets.
   *
   * The band is what prices the work; this is what was sewn. The payout
   * document prints one row per entry (`garmentName` turns SS028-MDBLU-S into
   * "Intal Scrubsuit Elite Midnight Blue Small"), and the entries sum to
   * `qty` exactly, so the line's own total is unchanged by how it is shown.
   *
   * Absent on a typed line, on every payout written before 2026-09-22, and on
   * the imported workbook history — all of which print the band row the way
   * they always did. A document that loses a column is a smaller document; one
   * that invents a colour is a wrong one.
   */
  skuQty?: readonly { sku: string; qty: number }[];
}

/** The four additions the form carries, above the work table. */
export interface IPayoutAdditions {
  /** Contractor Additional Charge — ₱ per set (tools, machine, utility). */
  additionalChargeRate: number;
  /** Registration & Sales Invoice uplift, as a fraction (0.12). BIR only. */
  birIncreasePct: number;
  /** Transpo Allowance, a flat peso amount (₱200 at 20+ sets for delivery). */
  transpoAllowance: number;
  /** The form's unlabelled fourth row. */
  otherAddition: number;
  otherAdditionLabel?: string;
}

/** The four deductions the form carries, below the additions. */
export interface IPayoutDeductions {
  cashAdvance: number;
  shippingFee: number;
  distributedProduct: number;
  others: number;
  othersLabel?: string;
}

export interface IPayoutTotals {
  /** I68 / I67 — sets across every work line. */
  qtyTotal: number;
  /** K68 / K67 — rate × qty across every work line. */
  workTotal: number;
  /** K70 — ₱20 × qtyTotal. */
  additionalCharge: number;
  /** K71 — 12% × workTotal on the BIR side; 0 on the non-BIR side. */
  birIncrease: number;
  /** K72 / K71 — the flat allowance. */
  transpo: number;
  /** K73 / K72 — the free row. */
  otherAddition: number;
  /** K74 — every addition. */
  additionsTotal: number;
  /** K81 / K78 — every deduction. */
  deductionsTotal: number;
  /** K83 — work + additional charge + 12%. Earnings, allowance excluded. */
  netAmount: number;
  /** K84 / K80 — what is actually paid out. */
  totalAmount: number;
}

/** The workbook's own defaults (PAYSLIP G70/G71/G72). */
export const DEFAULT_ADDITIONS: IPayoutAdditions = {
  additionalChargeRate: 20,
  birIncreasePct: 0.12,
  transpoAllowance: 0,
  otherAddition: 0,
};

export const NO_DEDUCTIONS: IPayoutDeductions = {
  cashAdvance: 0,
  shippingFee: 0,
  distributedProduct: 0,
  others: 0,
};

/**
 * The transpo allowance the form describes: ₱200, "Minimum of 20 sets FOR
 * DELIVER".
 *
 * IT WAS A CHECKBOX NOBODY TICKED. This used to be applied by the caller on
 * the grounds that the ledger does not record whether a payout was delivered
 * or collected — so the app offered the amount and waited for a human. What
 * that produced was a payslip for 38 sets with the allowance missing off it
 * (Mikko, 2026-09-15), because a figure that is always earned and never
 * defaulted is a figure somebody eventually forgets.
 *
 * THE LEDGER DOES RECORD THE THING THAT MATTERS. Every QC pass on the slip is
 * work that was carried in: BQC5 cannot pass a bundle that never arrived. So
 * the sets a payslip pays for ARE sets delivered, and the form's own rule —
 * at least twenty of them — reads straight off that count.
 *
 * ONCE PER PAYSLIP, NOT ONCE PER TWENTY. "Minimum of 20 sets" is a threshold,
 * not a rate: both of the real payslips this was matched against show the row
 * at ₱200 × 1 (28 sets and 38 sets), never a multiple.
 *
 * `transpoFor` is the default, not the last word — `PayrollRun` still lets a
 * clerk turn it off for a delivery that did not happen the usual way.
 */
export const TRANSPO_ALLOWANCE = 200;
export const TRANSPO_MIN_SETS = 20;

/** The allowance a payslip earns on its own delivered-set count. */
export function transpoFor(setsDelivered: number): number {
  return num(setsDelivered) >= TRANSPO_MIN_SETS ? TRANSPO_ALLOWANCE : 0;
}

/**
 * Sets on a payslip, for the WORK COMPLETED heading.
 *
 * Every set is paid on up to two bands — a design band and a pants band — so
 * the quantity total is tops PLUS trousers and is nobody's idea of "how many".
 * The design side is the usual answer, but it reads ZERO for a week that was
 * entirely PANTS ONLY, so this takes whichever side is larger.
 *
 * Exact for every payslip that is all full sets, all top only, or all pants
 * only — which is all of them so far. It undercounts only a payslip that
 * mixes a top-only AND a pants-only line, and even then it is a heading: the
 * QTY and TOTAL beneath it are derived from the lines and stay exact.
 */
export function payslipSets(lines: readonly IPayoutLine[]): number {
  let tops = 0, pants = 0;
  for (const l of lines) {
    if (l.group === "PANTS") pants += Number(l.qty) || 0;
    else tops += Number(l.qty) || 0;
  }
  return Math.max(tops, pants);
}

/**
 * What the payout covers as sets and pieces APART (Mikko, 2026-10-12: "it should be
 * reflected the Top Only and Pants Only, not only sets"). Every set earns a TOP band and
 * a PANTS band, so a TOP-band set is a full set or a Top Only piece, and a piece is told
 * by the pass's own plan (a payout written from now on) or the SKU's -TO- / -PO- marker
 * (older ones). `null` when nothing says pieces are involved, so the paper reads as before.
 */
export function payslipMix(lines: readonly IPayoutLine[]): { sets: number; topOnly: number; pantsOnly: number } | null {
  let tops = 0, topOnly = 0, pantsOnly = 0;
  for (const l of lines) {
    const isPants = l.group === "PANTS";
    if (!isPants) tops += Number(l.qty) || 0;
    const planOfPass = (plan: string | undefined, sku: string | undefined): string =>
      plan ?? planOfSku(sku);
    if (l.passes && l.passes.length > 0) {
      for (const x of l.passes) {
        const pl = planOfPass(x.plan, x.sku);
        if (!isPants && pl === "TOP") topOnly += x.sets;
        if (isPants && pl === "PANTS") pantsOnly += x.sets;
      }
    } else {
      for (const g of l.skuQty ?? []) {
        const pl = planOfSku(g.sku);
        if (!isPants && pl === "TOP") topOnly += g.qty;
        if (isPants && pl === "PANTS") pantsOnly += g.qty;
      }
    }
  }
  if (topOnly === 0 && pantsOnly === 0) return null;
  return { sets: Math.max(0, tops - topOnly), topOnly, pantsOnly };
}

/** "37 sets — 4 Top Only — 5 Pants Only": the dash, never a bar, on a paper somebody signs. */
export function payslipMixText(m: { sets: number; topOnly: number; pantsOnly: number }): string {
  return [
    m.sets > 0 ? `${m.sets} set${m.sets === 1 ? "" : "s"}` : "",
    m.topOnly > 0 ? `${m.topOnly} Top Only` : "",
    m.pantsOnly > 0 ? `${m.pantsOnly} Pants Only` : "",
  ].filter(Boolean).join(" — ");
}

/** Peso rounding — two decimals, and never `-0`. */
export function peso(n: number): number {
  const v = Math.round((Number(n) + Number.EPSILON) * 100) / 100;
  return Object.is(v, -0) ? 0 : v;
}

const num = (n: unknown): number => {
  const v = Number(n);
  return Number.isFinite(v) ? v : 0;
};

/** rate × qty for one line, to the centavo. */
export function lineAmount(line: Pick<IPayoutLine, "rate" | "qty">): number {
  return peso(num(line.rate) * num(line.qty));
}

/**
 * Every figure on one payslip.
 *
 * `birIncreasePct` is ignored outright on the non-BIR side rather than being
 * left to the caller to zero — the whole point of the distinction is that an
 * unregistered subcontractor issues no sales invoice, so there is nothing for
 * the uplift to reimburse.
 */
export function payoutTotals(
  lines: readonly IPayoutLine[],
  side: BirSide,
  additions: IPayoutAdditions = DEFAULT_ADDITIONS,
  deductions: IPayoutDeductions = NO_DEDUCTIONS
): IPayoutTotals {
  const qtyTotal = lines.reduce((s, l) => s + num(l.qty), 0);
  const workTotal = peso(lines.reduce((s, l) => s + lineAmount(l), 0));

  const additionalCharge = peso(num(additions.additionalChargeRate) * qtyTotal);
  const birIncrease = side === "BIR" ? peso(num(additions.birIncreasePct) * workTotal) : 0;
  const transpo = peso(num(additions.transpoAllowance));
  const otherAddition = peso(num(additions.otherAddition));
  const additionsTotal = peso(additionalCharge + birIncrease + transpo + otherAddition);

  const deductionsTotal = peso(
    num(deductions.cashAdvance) + num(deductions.shippingFee) +
    num(deductions.distributedProduct) + num(deductions.others)
  );

  return {
    qtyTotal,
    workTotal,
    additionalCharge,
    birIncrease,
    transpo,
    otherAddition,
    additionsTotal,
    deductionsTotal,
    netAmount: peso(workTotal + additionalCharge + birIncrease),
    totalAmount: peso(workTotal + additionsTotal - deductionsTotal),
  };
}

/**
 * The numeric part of a contractor ID — "IG00108" -> 108.
 *
 * The masterlist keeps this split out in its own column (P on the BIR sheet,
 * N on the non-BIR one) purely so the payout reference can use it. Derived
 * here instead, so an ID typed into the app needs no second field kept in
 * step with it. Returns null for anything that carries no digits, which
 * blocks the reference rather than producing "-9/4/2026".
 */
export function contractorSeq(contractorId: string | null | undefined): number | null {
  const m = String(contractorId ?? "").match(/(\d+)\s*$/);
  if (!m) return null;
  const n = Number(m[1]);
  return Number.isFinite(n) ? n : null;
}

/**
 * M/D/YYYY, unpadded — the shape VBA's `Range.Value` concatenation produced,
 * and therefore the shape five thousand existing payout references are in.
 * Matching it is not cosmetic: RECORDS OF PAYOUT is keyed on this string.
 *
 * Takes an ISO date (YYYY-MM-DD) and formats it by arithmetic rather than
 * through `Date`, because `new Date("2026-09-04")` is midnight UTC and prints
 * as the 3rd anywhere west of Greenwich — an off-by-one that would put a
 * payslip in the wrong week.
 */
export function formatRefDate(isoDate: string): string {
  const m = String(isoDate ?? "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return "";
  const [, y, mo, d] = m;
  return `${Number(mo)}/${Number(d)}/${y}`;
}

/**
 * The payout reference, exactly as the workbook builds it: the contractor's
 * sequence, a hyphen, and the last day of the working period — "63-6/14/2025".
 *
 * Returns null when either half is missing. A payout with no reference is
 * refused upstream rather than saved under a half-formed key, because the
 * reference is what the records sheet, the payslip PDF and the bank file all
 * join on.
 */
export function payoutRef(contractorId: string | null | undefined, periodEndIso: string): string | null {
  const seq = contractorSeq(contractorId);
  const date = formatRefDate(periodEndIso);
  if (seq === null || !date) return null;
  return `${seq}-${date}`;
}

/**
 * The payout reference, made safe to put in a URL path segment.
 *
 * A reference is "63-6/14/2025" and those slashes are the problem: encoded as
 * %2F they survive React Router, but a CDN, a proxy or Azure's own static
 * fallback is free to normalise %2F back into a path separator, at which
 * point /subcontractors/payslip/63-6%2F14%2F2025 becomes three segments and
 * the payslip 404s — on a hard refresh or a bookmarked link, which is exactly
 * how somebody opens a payslip they were sent. `~` is unreserved in RFC 3986,
 * appears in no reference, and travels through everything untouched.
 */
export function refToParam(ref: string): string {
  return String(ref ?? "").replace(/\//g, "~");
}

export function paramToRef(param: string): string {
  return String(param ?? "").replace(/~/g, "/");
}

/**
 * The file name the payslip PDF is saved under: reference then name, e.g.
 * "214-GUEVARRA, ALONA MERANA" — the workbook's `CONCAT(E13,E7)`, which is
 * what the existing Payslips folders are already full of. The browser takes
 * the print file name from the document title, so this IS the file name.
 */
export function payslipDocTitle(ref: string, contractorName: string): string {
  return `${ref}-${contractorName}`.replace(/[\\/:*?"<>|]/g, "-").trim();
}
